// Supabase Service - Authentication and Database
// Use CDN import for web compatibility (loaded in index.html)

// Supabase configuration
const SUPABASE_URL = 'https://chahzabowzvnhaqduxoh.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImNoYWh6YWJvd3p2bmhhcWR1eG9oIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjU3MTAzMTksImV4cCI6MjA4MTI4NjMxOX0.JHve4Ky1U828nSdJPw37qu5UE8GJe7GYZrVc-xB_V6o';

// Get Supabase createClient function
function getSupabaseClient() {
    if (typeof window !== 'undefined' && window.SupabaseClient && window.SupabaseClient.createClient) {
        // Browser: use global Supabase from CDN
        return window.SupabaseClient.createClient;
    }
    // If not available, throw error (will be caught and retried)
    throw new Error('Supabase client not available. Make sure Supabase is loaded from CDN.');
}

// Create Supabase client (will wait for CDN to load if needed)
let supabase = null;
function initSupabaseClient() {
    if (!supabase) {
        const createClient = getSupabaseClient();
        supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
            auth: {
                // Bypass navigator.locks which causes deadlock timeouts on
                // Cloudflare Pages and other single-tab deployments.
                // Uses a simple in-memory mutex instead — safe for single-tab apps.
                lock: (name, acquireTimeout, fn) => {
                    // Simple in-memory lock that never deadlocks
                    if (!initSupabaseClient._locks) initSupabaseClient._locks = {};
                    const locks = initSupabaseClient._locks;

                    const tryAcquire = () => {
                        if (!locks[name]) {
                            locks[name] = true;
                            return fn().finally(() => { delete locks[name]; });
                        }
                        // If lock held, wait and retry (up to acquireTimeout)
                        return new Promise((resolve, reject) => {
                            const start = Date.now();
                            const interval = setInterval(() => {
                                if (!locks[name]) {
                                    clearInterval(interval);
                                    locks[name] = true;
                                    resolve(fn().finally(() => { delete locks[name]; }));
                                } else if (Date.now() - start > acquireTimeout) {
                                    clearInterval(interval);
                                    reject(new Error(`Lock "${name}" acquire timeout`));
                                }
                            }, 50);
                        });
                    };
                    return tryAcquire();
                },
            },
        });
    }
    return supabase;
}

class SupabaseService {
    constructor() {
        this.client = null;
        this.currentUser = null;
        this._oauthInFlight = false;
        // Initialize client (wait for CDN if needed)
        this._initClient();
    }

    async _initClient() {
        // Wait for Supabase CDN to load if in browser
        if (typeof window !== 'undefined' && !window.SupabaseClient) {
            await new Promise((resolve) => {
                if (window.SupabaseClient) {
                    resolve();
                } else {
                    window.addEventListener('supabaseReady', resolve, { once: true });
                    // Timeout after 10 seconds (longer for slower connections)
                    setTimeout(() => {
                        console.error('[Supabase] CDN load timeout - Supabase may not be available');
                        resolve();
                    }, 10000);
                }
            });
        }

        // Now initialize the client (with retry logic)
        try {
            this.client = initSupabaseClient();
            this.setupAuthListener();
            // Handle OAuth callback and check existing session
            await this.initializeAuth();
        } catch (error) {
            console.error('[Supabase] Failed to initialize client:', error);
            // Retry once after a short delay
            await new Promise(resolve => setTimeout(resolve, 1000));
            if (window.SupabaseClient && window.SupabaseClient.createClient) {
                this.client = initSupabaseClient();
                this.setupAuthListener();
                await this.initializeAuth();
            } else {
                throw new Error('Supabase client initialization failed. Please refresh the page.');
            }
        }
    }

    /**
     * Initialize authentication - check for OAuth callback and existing session
     */
    async initializeAuth() {
        // First, handle OAuth callback if present
        await this.handleOAuthCallback();

        // Then, check for existing session
        const session = await this.getSession();
        if (session) {
            this.currentUser = session.user;
            await this.syncUserProfile();
        }
    }

    /**
     * Handle OAuth callback from URL hash
     */
    async handleOAuthCallback() {
        if (!this.client) {
            console.warn('[Supabase] Cannot handle OAuth callback - client not initialized');
            return;
        }
        // Check if we have OAuth tokens in the URL hash
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');

        if (accessToken && refreshToken) {
            try {
                // Set the session using the tokens from the URL
                const { data, error } = await this.client.auth.setSession({
                    access_token: accessToken,
                    refresh_token: refreshToken
                });

                if (error) {
                    console.error('Error setting session from OAuth callback:', error);
                } else if (data.session) {
                    this.currentUser = data.session.user;
                    await this.syncUserProfile();
                    // Clean up URL hash
                    window.history.replaceState(null, '', window.location.pathname + window.location.search);
                }
            } catch (error) {
                console.error('Error handling OAuth callback:', error);
            }
        }
    }

    /**
     * Setup auth state change listener
     */
    setupAuthListener() {
        if (!this.client) {
            console.warn('[Supabase] Cannot setup auth listener - client not initialized');
            return;
        }
        this.client.auth.onAuthStateChange((event, session) => {
            if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
                this.currentUser = session?.user || null;
                this.syncUserProfile();
            } else if (event === 'SIGNED_OUT') {
                this.currentUser = null;
            }
        });
    }

    /**
     * Get current session
     */
    async getSession() {
        if (!this.client) {
            console.warn('[Supabase] Client not initialized yet');
            return null;
        }
        const { data: { session } } = await this.client.auth.getSession();
        this.currentUser = session?.user || null;
        return session;
    }

    /**
     * Sign in with X (Twitter) OAuth
     * @param {boolean} usePopup - If true, opens OAuth in popup instead of redirecting
     */
    async signInWithX(usePopup = false) {
        try {
            // Prevent multiple concurrent OAuth attempts (can produce invalid/used request tokens)
            if (this._oauthInFlight) {
                return { success: false, error: 'Authentication already in progress. Please complete the existing X login window.' };
            }
            this._oauthInFlight = true;

            if (usePopup) {
                // Popup-based OAuth flow
                const res = await this.signInWithXPopup();
                // Popup flow returns control; clear in-flight flag here
                this._oauthInFlight = false;
                return res;
            } else {
                // Standard redirect flow (Supabase handles navigation)
                const { data, error } = await this.client.auth.signInWithOAuth({
                    provider: 'twitter',
                    options: {
                        // Use origin-only redirect so one allowlisted domain pattern works
                        // without requiring every individual app route in Supabase Auth settings.
                        redirectTo: window.location.origin,
                        skipBrowserRedirect: false
                    }
                });

                if (error) throw error;
                // Redirect flow will navigate away — auto-reset flag after
                // 30s in case the Supabase auth endpoint times out and the
                // user stays on the same page.
                setTimeout(() => { this._oauthInFlight = false; }, 30000);
                return { success: true, data };
            }
        } catch (error) {
            console.error('Error signing in with X:', error);
            this._oauthInFlight = false;
            return { success: false, error: error.message };
        }
    }

    /**
     * Sign in with X using popup window
     */
    async signInWithXPopup() {
        return new Promise((resolve) => {
            // Use origin-only redirect so one allowlisted domain pattern works
            // without requiring every individual app route in Supabase Auth settings.
            const redirectUrl = window.location.origin;

            console.log('[OAuth] Starting popup OAuth flow');
            console.log('[OAuth] Redirect URL:', redirectUrl);

            // Get OAuth URL without redirecting
            this.client.auth.signInWithOAuth({
                provider: 'twitter',
                options: {
                    redirectTo: redirectUrl,
                    skipBrowserRedirect: true
                }
            }).then(({ data, error }) => {
                if (error) {
                    console.error('[OAuth] Error getting OAuth URL:', error);
                    resolve({ success: false, error: error.message });
                    return;
                }

                if (!data || !data.url) {
                    console.error('[OAuth] No URL returned from Supabase. Data:', data);
                    resolve({ success: false, error: 'No OAuth URL returned from Supabase. Please check your Supabase Twitter OAuth configuration.' });
                    return;
                }

                console.log('[OAuth] OAuth URL received (first 150 chars):', data.url.substring(0, 150));

                // Validate URL format
                try {
                    const urlObj = new URL(data.url);
                    if (!urlObj.hostname.includes('twitter.com') && !urlObj.hostname.includes('x.com')) {
                        console.warn('[OAuth] Warning: OAuth URL does not point to Twitter/X:', urlObj.hostname);
                    }
                } catch (e) {
                    console.error('[OAuth] Invalid OAuth URL format:', e);
                    resolve({ success: false, error: 'Invalid OAuth URL format' });
                    return;
                }

                // Open popup window
                const width = 600;
                const height = 700;
                const left = (window.screen.width - width) / 2;
                const top = (window.screen.height - height) / 2;

                const popup = window.open(
                    data.url,
                    'X Authentication',
                    `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
                );

                if (!popup) {
                    resolve({ success: false, error: 'Popup blocked. Please allow popups for this site.' });
                    return;
                }

                // Poll for popup URL changes (when it redirects back to our callback)
                let lastPopupUrl = '';
                const checkInterval = setInterval(async () => {
                    try {
                        // Check if popup was closed
                        if (popup.closed) {
                            clearInterval(checkInterval);
                            // Check if auth succeeded before popup closed
                            const session = await this.getSession();
                            if (session && session.user) {
                                resolve({ success: true, data: { session } });
                            } else {
                                resolve({ success: false, error: 'Authentication cancelled or failed' });
                            }
                            return;
                        }

                        // Try to access popup location (will throw if cross-origin)
                        let popupUrl;
                        let popupHash;
                        try {
                            popupUrl = popup.location.href;
                            popupHash = popup.location.hash;

                            // If URL changed and we can access it, check if it's our callback
                            if (popupUrl !== lastPopupUrl && popupUrl.startsWith(redirectUrl)) {
                                lastPopupUrl = popupUrl;

                                // Wait a moment for the page to fully load
                                await new Promise(resolve => setTimeout(resolve, 100));

                                // Check hash for tokens
                                if (popupHash && popupHash.includes('access_token')) {
                                    clearInterval(checkInterval);

                                    // Parse the hash and set session
                                    const hashParams = new URLSearchParams(popupHash.substring(1));
                                    const accessToken = hashParams.get('access_token');
                                    const refreshToken = hashParams.get('refresh_token');

                                    if (accessToken && refreshToken) {
                                        try {
                                            // Set session using tokens from popup
                                            const { data: sessionData, error: sessionError } = await this.client.auth.setSession({
                                                access_token: accessToken,
                                                refresh_token: refreshToken
                                            });

                                            if (sessionError) {
                                                popup.close();
                                                resolve({ success: false, error: sessionError.message });
                                                return;
                                            }

                                            if (sessionData.session) {
                                                this.currentUser = sessionData.session.user;
                                                await this.syncUserProfile();

                                                // Close popup after a brief delay
                                                setTimeout(() => {
                                                    if (!popup.closed) popup.close();
                                                }, 500);

                                                resolve({ success: true, data: { session: sessionData.session } });
                                                return;
                                            }
                                        } catch (err) {
                                            popup.close();
                                            resolve({ success: false, error: err.message });
                                            return;
                                        }
                                    } else {
                                        popup.close();
                                        resolve({ success: false, error: 'Failed to extract authentication tokens from callback' });
                                        return;
                                    }
                                }
                            }
                        } catch (e) {
                            // Cross-origin - still on Twitter/X, continue polling
                            // This is expected when popup is on Twitter's domain
                            return;
                        }
                    } catch (err) {
                        // Ignore errors from cross-origin access
                        // Continue polling
                    }
                }, 300); // Check more frequently for better responsiveness

                // Also listen for postMessage from popup (backup method)
                const messageListener = (event) => {
                    // Security: Only accept messages from our origin
                    if (event.origin !== window.location.origin) return;

                    if (event.data && event.data.type === 'OAUTH_SUCCESS') {
                        clearInterval(checkInterval);
                        window.removeEventListener('message', messageListener);
                        if (!popup.closed) popup.close();
                        resolve({ success: true, data: event.data });
                    } else if (event.data && event.data.type === 'OAUTH_ERROR') {
                        clearInterval(checkInterval);
                        window.removeEventListener('message', messageListener);
                        if (!popup.closed) popup.close();
                        resolve({ success: false, error: event.data.error || 'Authentication failed' });
                    }
                };

                window.addEventListener('message', messageListener);

                // Cleanup after 5 minutes (timeout)
                setTimeout(() => {
                    if (!popup.closed) {
                        popup.close();
                    }
                    clearInterval(checkInterval);
                    window.removeEventListener('message', messageListener);
                    resolve({ success: false, error: 'Authentication timeout' });
                }, 5 * 60 * 1000);
            });
        });
    }

    /**
     * Sign out
     */
    async signOut() {
        try {
            const { error } = await this.client.auth.signOut();
            if (error) throw error;
            this.currentUser = null;
            return { success: true };
        } catch (error) {
            console.error('Error signing out:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Sync user profile after authentication
     */
    async syncUserProfile() {
        if (!this.currentUser) {
            console.log('[Supabase] No current user, skipping profile sync');
            return;
        }

        // Ensure we have a valid session before making requests
        const session = await this.getSession();
        if (!session) {
            console.log('[Supabase] No session found, cannot sync profile');
            return;
        }

        try {
            const userMetadata = this.currentUser.user_metadata || {};

            // Extract X/Twitter data from metadata
            const xHandle = userMetadata.preferred_username || userMetadata.user_name ||
                userMetadata.user_metadata?.preferred_username || userMetadata.user_metadata?.user_name;
            const xUserId = userMetadata.provider_id || userMetadata.sub;
            const xUsername = userMetadata.full_name || userMetadata.name ||
                userMetadata.user_metadata?.full_name || userMetadata.user_metadata?.name;

            // enhanced avatar extraction
            let xAvatarUrl = userMetadata.avatar_url || userMetadata.picture ||
                userMetadata.user_metadata?.avatar_url || userMetadata.user_metadata?.picture;

            // Check specifically for Twitter's profile_image_url_https which is often where the real image is
            if (!xAvatarUrl && (userMetadata.profile_image_url_https || userMetadata.user_metadata?.profile_image_url_https)) {
                xAvatarUrl = userMetadata.profile_image_url_https || userMetadata.user_metadata?.profile_image_url_https;
            }

            // If we still don't have it, try to find it in the raw 'iss' or custom claims if present, 
            // but usually the above covers it. 
            // Also, sometimes the URL is 'normal' size, which is small. We might want 'original' but let's stick to what is provided for now.

            // Try to fetch existing user
            const { data: userData, error: fetchError } = await this.client
                .from('users')
                .select('*')
                .eq('id', this.currentUser.id)
                .maybeSingle(); // Use maybeSingle instead of single to avoid error if not found

            if (fetchError) {
                console.error('[Supabase] Error fetching user profile:', fetchError);
                return;
            }

            if (!userData) {
                // User doesn't exist, create profile
                console.log('[Supabase] Creating new user profile for:', this.currentUser.id);
                const { data: newUser, error: insertError } = await this.client
                    .from('users')
                    .insert({
                        id: this.currentUser.id,
                        x_handle: xHandle,
                        x_user_id: xUserId,
                        x_username: xUsername,
                        x_avatar_url: xAvatarUrl
                    })
                    .select()
                    .single();

                if (insertError) {
                    console.error('[Supabase] Error creating user profile:', insertError);
                    // Log more details for debugging
                    console.error('[Supabase] Insert error details:', {
                        code: insertError.code,
                        message: insertError.message,
                        details: insertError.details,
                        hint: insertError.hint
                    });
                } else {
                    console.log('[Supabase] User profile created successfully:', newUser);
                }
            } else {
                // Update existing profile if metadata changed
                const updates = {};

                if (xHandle && xHandle !== userData.x_handle) {
                    updates.x_handle = xHandle;
                }
                if (xAvatarUrl && xAvatarUrl !== userData.x_avatar_url) {
                    updates.x_avatar_url = xAvatarUrl;
                }
                if (xUsername && xUsername !== userData.x_username) {
                    updates.x_username = xUsername;
                }
                if (xUserId && xUserId !== userData.x_user_id) {
                    updates.x_user_id = xUserId;
                }

                if (Object.keys(updates).length > 0) {
                    console.log('[Supabase] Updating user profile:', updates);
                    const { error: updateError } = await this.client
                        .from('users')
                        .update(updates)
                        .eq('id', this.currentUser.id);

                    if (updateError) {
                        console.error('[Supabase] Error updating user profile:', updateError);
                    }
                }
            }
        } catch (error) {
            console.error('[Supabase] Error syncing user profile:', error);
        }
    }

    /**
     * Get current user profile
     */
    async getUserProfile() {
        if (!this.currentUser) return null;

        try {
            const { data, error } = await this.client
                .from('users')
                .select('*')
                .eq('id', this.currentUser.id)
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('Error fetching user profile:', error);
            return null;
        }
    }

    /**
     * Get user wallets with points
     */
    async getUserWallets() {
        if (!this.currentUser) return [];

        try {
            const { data, error } = await this.client
                .from('wallets')
                .select('id, user_id, wallet_address, network, points, created_at')
                .eq('user_id', this.currentUser.id)
                .order('points', { ascending: false, nullsLast: true })
                .order('created_at', { ascending: false });

            if (error) throw error;
            return data || [];
        } catch (error) {
            console.error('Error fetching wallets:', error);
            return [];
        }
    }

    /**
     * Update wallet points (for backend use or manual updates)
     */
    async updateWalletPoints(walletAddress, points) {
        if (!this.currentUser) {
            return { success: false, error: 'Not authenticated' };
        }

        try {
            const { data, error } = await this.client
                .from('wallets')
                .update({ points: points })
                .eq('wallet_address', walletAddress)
                .eq('user_id', this.currentUser.id)
                .select()
                .single();

            if (error) throw error;
            return { success: true, data };
        } catch (error) {
            console.error('Error updating wallet points:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Add wallet address
     */
    async addWallet(walletAddress, network = 'base') {
        if (!this.currentUser) {
            return { success: false, error: 'Not authenticated' };
        }

        try {
            // Check wallet limit
            const wallets = await this.getUserWallets();
            if (wallets.length >= 5) {
                return { success: false, error: 'Maximum 5 wallets allowed per user' };
            }

            // Check if wallet already exists
            const existingWallet = wallets.find(w => w.wallet_address.toLowerCase() === walletAddress.toLowerCase());
            if (existingWallet) {
                return { success: false, error: 'Wallet already connected' };
            }

            const { data, error } = await this.client
                .from('wallets')
                .insert({
                    user_id: this.currentUser.id,
                    wallet_address: walletAddress,
                    network: network
                })
                .select()
                .single();

            if (error) throw error;
            return { success: true, data };
        } catch (error) {
            console.error('Error adding wallet:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Remove wallet
     */
    async removeWallet(walletId) {
        if (!this.currentUser) {
            return { success: false, error: 'Not authenticated' };
        }

        try {
            const { error } = await this.client
                .from('wallets')
                .delete()
                .eq('id', walletId)
                .eq('user_id', this.currentUser.id);

            if (error) throw error;
            return { success: true };
        } catch (error) {
            console.error('Error removing wallet:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Get user referral code
     */
    async getUserReferralCode() {
        if (!this.currentUser) return null;

        try {
            const { data, error } = await this.client
                .from('referral_codes')
                .select('referral_code')
                .eq('user_id', this.currentUser.id)
                .single();

            if (error) throw error;
            return data?.referral_code || null;
        } catch (error) {
            console.error('Error fetching referral code:', error);
            return null;
        }
    }

    /**
     * Get user points (sum of points from all connected wallets)
     */
    async getUserPoints() {
        if (!this.currentUser) return null;

        try {
            // Get all wallets for this user
            const wallets = await this.getUserWallets();

            // Calculate total points from all wallets (base points)
            const totalWalletPoints = wallets.reduce((sum, wallet) => {
                return sum + parseFloat(wallet.points || 0);
            }, 0);

            // Get user's total points, base_points, referral bonus, and rank from points table
            const { data: userPoints, error: userPointsError } = await this.client
                .from('points')
                .select('points, base_points, referral_bonus, rank')
                .eq('user_id', this.currentUser.id)
                .maybeSingle();

            const totalPoints = userPoints?.points || 0; // This is base_points + referral_bonus
            const basePoints = userPoints?.base_points || totalWalletPoints; // Fallback to wallet sum if base_points is null
            const referralBonus = userPoints?.referral_bonus || 0;
            const rank = userPoints?.rank || null;

            // Return total points (base + referral), with breakdown
            return {
                points: parseFloat(totalPoints), // Total = base_points + referral_bonus
                base_points: parseFloat(basePoints),
                referral_bonus: parseFloat(referralBonus),
                rank: rank,
                wallet_points: totalWalletPoints // Keep track of wallet-only points for reference
            };
        } catch (error) {
            console.error('Error fetching points:', error);
            return { points: 0, base_points: 0, referral_bonus: 0, rank: null, wallet_points: 0 };
        }
    }

    /**
     * Get leaderboard (using materialized view for performance)
     */
    async getLeaderboard(limit = 100, offset = 0) {
        try {
            // Try to use materialized view first (faster)
            const { data: mvData, error: mvError } = await this.client
                .from('leaderboard_mv')
                .select('*')
                .order('total_points', { ascending: false })
                .range(offset, offset + limit - 1);

            if (!mvError && mvData) {
                // Transform materialized view data to match expected format
                return mvData.map(entry => ({
                    points: entry.total_points,
                    referral_bonus: entry.referral_bonus,
                    rank: entry.rank,
                    user: {
                        id: entry.user_id,
                        x_user_id: entry.x_user_id,
                        x_handle: entry.x_handle,
                        x_username: entry.x_username,
                        x_avatar_url: entry.x_avatar_url
                    }
                }));
            }

            // Fallback to regular query if materialized view fails
            const { data, error } = await this.client
                .from('points')
                .select(`
                    points,
                    referral_bonus,
                    rank,
                    user:users!inner(id, x_user_id, x_handle, x_username, x_avatar_url)
                `)
                .order('points', { ascending: false })
                .range(offset, offset + limit - 1);

            if (error) throw error;
            return data || [];
        } catch (error) {
            console.error('Error fetching leaderboard:', error);
            return [];
        }
    }

    /**
     * Use referral code
     */
    async useReferralCode(referralCode) {
        if (!this.currentUser) {
            return { success: false, error: 'Not authenticated' };
        }

        try {
            // Single RPC call (avoids multi-query hangs + avoids RLS issues)
            const timeoutMs = 12000;
            const rpcPromise = this.client.rpc('apply_referral_code', { p_code: String(referralCode || '') });
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error(`Apply referral code timed out after ${timeoutMs}ms`)), timeoutMs)
            );

            const { data, error } = await Promise.race([rpcPromise, timeoutPromise]);
            if (error) throw error;

            if (data?.success) {
                return { success: true, data };
            }
            return { success: false, error: 'Failed to apply referral code' };
        } catch (error) {
            console.error('Error using referral code:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Get current user's referrer (display info), if any.
     * Uses a SECURITY DEFINER RPC to avoid widening users-table RLS.
     */
    async getMyReferrer() {
        if (!this.currentUser) return null;
        try {
            const { data, error } = await this.client.rpc('get_my_referrer');
            if (error) throw error;
            if (Array.isArray(data) && data.length) return data[0];
            return data || null;
        } catch (e) {
            console.warn('Error fetching my referrer:', e);
            return null;
        }
    }
}

// Export singleton instance
const supabaseService = new SupabaseService();
export default supabaseService;
